// utils/zoomOut.ts
import { AnimationHistoryEntry } from "../../../backend/useAnimate";
import { uploadBlobToStorage } from "../../../backend/storageHelpers";
import { supabase } from "../../../backend/supabase";

/**
 * Creates a "zoomed out" version of the reference image by:
 * 1. Scaling the original image to 80% of its size.
 * 2. Centering it on a white background of the original dimensions.
 */
export const zoomOutImage = async (
    currentEntry: AnimationHistoryEntry,
    animationId: string,
    createHistoryEntry: any
): Promise<AnimationHistoryEntry | null> => {
    try {
        const imageUrl = currentEntry.reference_image;
        
        // 1. Load the original image to get dimensions
        const img = new Image();
        img.crossOrigin = "anonymous";
        
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imageUrl;
        });

        const originalWidth = img.naturalWidth;
        const originalHeight = img.naturalHeight;

        // 2. Setup Canvas
        const canvas = document.createElement("canvas");
        canvas.width = originalWidth;
        canvas.height = originalHeight;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
            throw new Error("Could not get canvas context");
        }

        // 3. Draw white background
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, originalWidth, originalHeight);

        // 4. Calculate 80% scale and centering
        const scale = 0.8;
        const newWidth = originalWidth * scale;
        const newHeight = originalHeight * scale;
        const xOffset = (originalWidth - newWidth) / 2;
        const yOffset = (originalHeight - newHeight) / 2;

        // 5. Draw scaled image
        ctx.drawImage(img, xOffset, yOffset, newWidth, newHeight);

        // 6. Convert to Blob and Upload
        const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob((b) => resolve(b), "image/png");
        });

        if (!blob) {
            throw new Error("Failed to create blob from canvas");
        }

        const STORAGE_FOLDER = 'animation-assets';
        const uploadedImageUrl = await uploadBlobToStorage(
            supabase,
            blob,
            'zoom-out-reference-image',
            STORAGE_FOLDER,
            'image/png'
        );

        // 7. Create new history entry with the new reference image
        // We keep the animation_images and other metadata from the current entry
        const newEntry = await createHistoryEntry(
            animationId,
            currentEntry.user_prompt,
            uploadedImageUrl,
            currentEntry.animation_images || [],
            currentEntry.enhanced_prompt || undefined,
            currentEntry.animation_video || undefined,
            currentEntry.reference_image_description || undefined
        );

        return newEntry;

    } catch (error) {
        console.error("Error in zoomOutImage utility:", error);
        return null;
    }
};